"use client"

import { useState, useEffect } from "react"
import {
  MagnifyingGlass,
  DownloadSimple,
  FunnelSimple,
  X,
  ArrowClockwise,
  WarningCircle,
  SpinnerGap,
  CalendarBlank,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  PlusCircle,
  Eye,
  User,
  Shield,
  Clock,
  Crown,
  CreditCard,
  ArrowDownRight,
  ArrowUpRight,
  CaretDown,
  CurrencyDollar,
  Pencil,
  Tag,
  Activity
} from "phosphor-react"

import WithdrawalRequestModal from "./WithdrawalRequestModal"
import TransactionDetailModal from "./TransactionDetailModal"
import ChangeStatusModal from "./ChangeStatusModal"
import { DateRangePicker, SearchableSelect } from "../../components"
import creditAccountsService from "../../services/creditAccountsService"
import creditAccountHistoryService from "../../services/creditAccountHistoryService"
import clientsService from "../../services/clientsService"
import toast from "react-hot-toast"
import { useCurrentUser } from "../../utils/context/UserProvider"
import { useAccessChecker } from "../../utils/Functions/Authorization"

function BalanceRecords() {
  const { checkAccess } = useAccessChecker()
  const { currentUser } = useCurrentUser()

  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [dateRange, setDateRange] = useState({ start: "", end: "" })
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [filters, setFilters] = useState({
    transactionType: "",
    status: "",
    paymentMethod: "",
    user: "",
  })

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalPages: 1,
    totalItems: 0,
  })

  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false)
  const [showTransactionDetailModal, setShowTransactionDetailModal] = useState(false)
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [done, setDone] = useState(false)

  const [accountInfo, setAccountInfo] = useState({
    name: "Credit Account",
    id: "",
    balance: 0,
    currency: "USD",
    status: "active",
  })

  const [userOptions, setUserOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [filtersActive, setFiltersActive] = useState(false)

  const [hasUserAccount, setHasUserAccount] = useState(false)
  const [userAccount, setUserAccount] = useState(null)

  // ✅ NEW: totals for cards
  const [totals, setTotals] = useState({
    totalDeposit: 0,
    totalRefund: 0,
  })

  const MIN_BALANCE = 500
  const isNegative = accountInfo.balance < 0
  const isBelowMin = !isNegative && accountInfo.balance < MIN_BALANCE

  /* ────────────────────────────── Title ────────────────────────────── */
  useEffect(() => {
    const title = currentUser?.role?.name === "Client" ? "Transaction History" : "Credit Account History"
    const event = new CustomEvent("setPageTitle", { detail: { title } })
    window.dispatchEvent(event)

    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Dashboard" },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [currentUser?.role?.name])

  /* ───────────────────── Fetch initial (role-based) ───────────────────── */
  useEffect(() => {
    if (currentUser?.role?.name === "Client") {
      fetchUserAccount()
    } else {
      fetchClients()
    }
  }, [currentUser])

  /* ───────────────────── Filters active? ───────────────────── */
  useEffect(() => {
    const hasActive =
      searchQuery !== "" ||
      dateRange.start !== "" ||
      dateRange.end !== "" ||
      filters.transactionType !== "" ||
      filters.status !== "" ||
      filters.paymentMethod !== "" ||
      filters.user !== ""

    setFiltersActive(hasActive)
  }, [searchQuery, dateRange, filters])

  /* ───────────────────── Fetch transactions on changes ───────────────────── */
  useEffect(() => {
    if (currentUser?.role?.name === "Client" && hasUserAccount) {
      fetchUserTransactions()
    } else if (currentUser?.role?.name !== "Client") {
      fetchTransactions()
    }
  }, [done, pagination.currentPage, pagination.pageSize, hasUserAccount, currentUser])

  /* ────────────────────────────── API Calls ────────────────────────────── */

  const fetchUserAccount = async () => {
    try {
      setLoading(true)
      const response = await creditAccountsService.getUserAccount({})
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {
        const account = responseData.data
        setUserAccount(account)
        setHasUserAccount(true)
        setAccountInfo({
          name: account.account_name,
          id: account._id,
          balance: Number.parseFloat(account.balance) || 0,
          currency: account.account_currency || "USD",
          status: account.status || "active",
          account_id: account.account_id,
        })
      } else {
        setHasUserAccount(false)
        setUserAccount(null)
      }
    } catch (error) {
      console.error("Failed to fetch user account:", error)
      setHasUserAccount(false)
      setUserAccount(null)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserTransactions = async () => {
    try {
      setLoading(true)

      const payload = {
        pagination: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        },
        dateRange: dateRange.start || dateRange.end ? dateRange : null,
      }

      if (filters.transactionType === "deposit") {
        payload.type = "Deposit"
      } else if (filters.transactionType === "withdrawal") {
        payload.type = "Withdrawal"
      }

      if (filters.status) {
        payload.status = filters.status
      }

      if (filters.paymentMethod) {
        const paymentMethodMap = {
          "Credit Card": "credit_card",
          "Bank Transfer": "bank_transfer",
          PayPal: "paypal",
          Payoneer: "payoneer",
          USDT: "usdt",
        }
        payload.payment_method = paymentMethodMap[filters.paymentMethod]
      }

      const response = await creditAccountHistoryService.getUserTransactions(payload)
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {
        const formattedTransactions = responseData.data.map((transaction) => {
          const amount = Number.parseFloat(transaction.amount)

          return {
            id: transaction.transaction_id || transaction._id,
            type: transaction.type.toLowerCase(),
            amount,
            currency: transaction.currency || "USD",
            date: transaction.createdAt,
            status: transaction.status.toLowerCase(),
            reference:
              transaction.reference || `${transaction.type} via ${formatPaymentMethod(transaction.payment_method)}`,
            paymentMethod: formatPaymentMethod(transaction.payment_method),
            details: transaction.payment_details || {},
            statusChangedBy: transaction.processed_by
              ? {
                name: transaction.processed_by.fullName || transaction.processed_by.email || "Admin",
                email: transaction.processed_by.email,
                role: transaction.processed_by.role?.name || "Admin",
                id: transaction.processed_by._id,
              }
              : null,
            statusChangedAt: transaction.status_changed_at,
            previousStatus: transaction.previous_status,
            adminNotes: transaction.admin_notes,
            rejectionReason: transaction.rejection_reason,
            rawData: transaction,
          }
        })

        setTransactions(formattedTransactions)

        // ✅ NEW: totals for cards (if backend returns totals)
        if (responseData.totals) {
          setTotals({
            totalDeposit: Number(responseData.totals.totalDeposit) || 0,
            totalRefund: Number(responseData.totals.totalRefund) || 0,
            totalWithdrawal: Number(responseData.totals.totalWithdrawal) || 0,
          })
        } else {
          setTotals({ totalDeposit: 0, totalRefund: 0, totalWithdrawal: 0 })
        }

        if (responseData.pagination) {
          setPagination({
            currentPage: responseData.pagination.currentPage,
            pageSize: responseData.pagination.pageSize,
            totalPages: responseData.pagination.totalPages,
            totalItems: responseData.pagination.totalItems,
          })
        }
      }
    } catch (error) {
      console.error("Failed to fetch user transactions:", error)
      toast.error("Error fetching transactions")
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    try {
      if (currentUser?.role?.name === "Client") return

      setLoadingOptions(true)
      const response = await clientsService.searchClients()
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {
        const dataClients = responseData.data.map((item) => ({
          id: item._id,
          name: item.reference || item.fullName,
          value: item._id,
        }))
        setUserOptions(dataClients)
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error)
      toast.error(error.response?.data?.message || "Failed to load clients")
    } finally {
      setLoadingOptions(false)
    }
  }

  const fetchTransactions = async () => {
    try {
      setLoading(true)

      const payload = {
        searchValue: searchQuery,
        pagination: {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        },
        dateRange: dateRange.start || dateRange.end ? dateRange : null,
      }

      if (filters.transactionType === "deposit") {
        payload.type = "Deposit"
      } else if (filters.transactionType === "withdrawal") {
        payload.type = "Withdrawal"
      }

      if (filters.status) {
        payload.status = filters.status
      }

      if (filters.paymentMethod) {

        payload.payment_method = filters.paymentMethod
      }

      if (filters.user) {
        payload.user = filters.user
      }

      const response = await creditAccountHistoryService.search(payload)
      const responseData = response.data

      if (responseData && responseData.statusCode === 200) {
        const formattedTransactions = responseData.data.map((transaction) => {
          const amount = Number.parseFloat(transaction.amount)

          return {
            id: transaction.transaction_id || transaction._id,
            type: transaction.type.toLowerCase(),
            amount,
            currency: transaction.currency || "USD",
            date: transaction.createdAt,
            status: transaction.status.toLowerCase(),
            reference:
              transaction.reference || `${transaction.type} via ${formatPaymentMethod(transaction.payment_method)}`,
            paymentMethod: formatPaymentMethod(transaction.payment_method),
            details: transaction.payment_details || {},
            clientName: transaction.user?.reference || transaction.user?.fullName || "Unknown",
            accountName: transaction.credit_account?.account_name || "Unknown Account",
            statusChangedBy: transaction.processed_by
              ? {
                name: transaction.processed_by.fullName || transaction.processed_by.email || "Admin",
                email: transaction.processed_by.email,
                role: transaction.processed_by.role?.name || "Admin",
                id: transaction.processed_by._id,
              }
              : null,
            statusChangedAt: transaction.status_changed_at,
            previousStatus: transaction.previous_status,
            adminNotes: transaction.admin_notes,
            rejectionReason: transaction.rejection_reason,
            rawData: transaction,
          }
        })

        setTransactions(formattedTransactions)

        // ✅ NEW: totals for cards (from backend search)
        if (responseData.totals) {
          setTotals({
            totalDeposit: Number(responseData.totals.totalDeposit) || 0,
            totalRefund: Number(responseData.totals.totalRefund) || 0,
          })
        } else {
          setTotals({ totalDeposit: 0, totalRefund: 0 })
        }

        if (responseData.pagination) {
          setPagination({
            currentPage: responseData.pagination.currentPage,
            pageSize: responseData.pagination.pageSize,
            totalPages: responseData.pagination.totalPages,
            totalItems: responseData.pagination.totalItems,
          })
        }
      } else {
        toast.error("Failed to fetch transactions")
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error)
      toast.error("Error fetching transactions")
    } finally {
      setLoading(false)
    }
  }

  /* ────────────────────────────── Helpers ────────────────────────────── */

  const formatPaymentMethod = (method) => {
    if (!method) return "Unknown"

    const methodMap = {
      credit_card: "Credit Card",
      bank_transfer: "Bank Transfer",
      paypal: "PayPal",
      payoneer: "Payoneer",
      usdt: "USDT",
    }

    return methodMap[method] || method.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())
  }

  const transactionTypeOptions = [
    { id: 1, name: "All Transactions", value: "" },
    { id: 2, name: "Deposits", value: "deposit" },
    { id: 3, name: "Refunds", value: "withdrawal" },
  ]

  const statusOptions = [
    { id: 1, name: "All Statuses", value: "" },
    { id: 2, name: "Completed", value: "completed" },
    { id: 3, name: "Pending", value: "pending" },
    { id: 4, name: "Rejected", value: "rejected" },
  ]

  const paymentMethodOptions = [
    { id: 1, name: "All Payment Methods", value: "" },
    { id: 2, name: "Credit Card", value: "credit_card" },
    { id: 3, name: "Bank Transfer", value: "bank_transfer" },
    { id: 4, name: "PayPal", value: "paypal" },
    { id: 5, name: "Payoneer", value: "payoneer" },
    { id: 6, name: "Crypto", value: "crypto" },
  ]

  const formattedUserOptions = [
    { id: 0, name: "All Clients", value: "" },
    ...userOptions.map((user, index) => ({
      id: index + 1,
      name: user.name,
      value: user.value,
    })),
  ]

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const handleDateRangeChange = (range) => setDateRange(range)

  const clearFilters = () => {
    setFilters({
      transactionType: "",
      status: "",
      paymentMethod: "",
      user: "",
    })
    setSearchQuery("")
    setDateRange({ start: "", end: "" })
  }

  const applyFilters = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }))
    if (currentUser?.role?.name === "Client") {
      fetchUserTransactions()
    } else {
      fetchTransactions()
    }
  }

  const resetAndRefresh = () => {
    clearFilters()
    setPagination((p) => ({ ...p, currentPage: 1 }))
    setTimeout(() => {
      if (currentUser?.role?.name === "Client") {
        fetchUserTransactions()
      } else {
        fetchTransactions()
      }
    }, 0)
  }

  const handleSearch = () => {
    setPagination((p) => ({ ...p, currentPage: 1 }))
    if (currentUser?.role?.name === "Client") {
      fetchUserTransactions()
    } else {
      fetchTransactions()
    }
  }



  const handleRequestWithdrawal = () => {
    if (currentUser?.role?.name === "Client" && !hasUserAccount) {
      toast.error("Please create a credit account first")
      return
    }
    setShowWithdrawalModal(true)
  }

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction)
    setShowTransactionDetailModal(true)
  }

  const handleChangeStatus = (transaction) => {
    setSelectedTransaction(transaction)
    setShowChangeStatusModal(true)
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return
    setPagination((p) => ({ ...p, currentPage: newPage }))
  }

  const handlePageSizeChange = (e) => {
    const newSize = Number.parseInt(e.target.value)
    setPagination((p) => ({ ...p, pageSize: newSize, currentPage: 1 }))
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-100 dark:border-green-800/60"
      case "pending":
        return "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-100 dark:border-yellow-800/60"
      case "rejected":
        return "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-100 dark:border-red-800/60"
      default:
        return "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-300 border border-neutral-100 dark:border-neutral-800/70"
    }
  }

  const getStatusDotClass = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-500"
      case "pending":
        return "bg-yellow-500"
      case "rejected":
        return "bg-red-500"
      default:
        return "bg-neutral-500"
    }
  }

  const formatStatusChangeDate = (raw) => {
    if (!raw) return "N/A"
    const dateString = String(raw).trim()
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const dotDateRE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
    const hasExplicitTime = /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString)

    let d
    const m = dateString.match(dotDateRE)
    if (m) {
      const [, dd, mm, yyyy, HH, MM] = m
      d = new Date(
        parseInt(yyyy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        HH ? parseInt(HH, 10) : 0,
        MM ? parseInt(MM, 10) : 0,
        0,
        0
      )
    } else {
      d = new Date(dateString)
    }
    if (isNaN(d.getTime())) return raw

    const datePart = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone,
    }).format(d)

    if (!hasExplicitTime) return datePart

    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    })

    const parts = timeFormatter.formatToParts(d)
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00"
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00"

    const hourNum = parseInt(hour, 10)
    const suffix = hourNum < 12 ? "AM" : "PM"

    const timePart = `${hour}:${minute} ${suffix}`

    return `${datePart}, ${timePart}`
  }

  /* ───────────────────── Client: no account view ───────────────────── */
  if (currentUser?.role?.name === "Client" && !hasUserAccount && !loading) {
    return (
      <div className="m-6 relative flex border rounded-xl p-6 bg-white border-neutral-200 dark:bg-neutral-950 dark:border-neutral-900 items-center justify-center">
        <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
          <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
            <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
          </div>
          <h2 className="text-xl font-[400] text-neutral-900 dark:text-white mb-2">No Credit Account Found</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
            You need to create a credit account first to view your transaction history.
          </p>
          {checkAccess("creditsAccounts", "create") && (
            <button
              onClick={() => (window.location.href = "/credit-accounts")}
              className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <PlusCircle size={18} />
              <span className="text-sm">Create Account</span>
            </button>
          )}
        </div>
      </div>
    )
  }

  /* ────────────────────────────── Render ────────────────────────────── */

  const displayCurrency = currentUser?.role?.name === "Client" ? accountInfo.currency : "USD"

  return (
    <div className="lg:p-7 p-3 max-w-full mx-auto">
      {/* Account Info for clients with account */}
      {currentUser?.role?.name === "Client" && hasUserAccount && (
        <div className="border border-neutral-150 dark:border-neutral-900 rounded-xl p-6 mb-6 bg-white dark:bg-neutral-950/80">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Crown size={18} className="text-amber-500" />
                <h1 className="text-lg font-[400] text-neutral-800 dark:text-white">
                  {accountInfo.name}
                </h1>
              </div>
              <p className="text-neutral-600 text-sm dark:text-neutral-400 mt-1">
                Account ID: {accountInfo.account_id} • Your only credit account
              </p>
            </div>

            <div className="flex flex-col items-start md:items-end">
              <div className="text-xs uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
                Available Balance
              </div>
              <div
                className={`mt-1 text-2xl font-[500] flex items-center gap-1 ${isNegative
                  ? "text-red-600 dark:text-red-500"
                  : isBelowMin
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-neutral-900 dark:text-white"
                  }`}
              >
                <CurrencyDollar size={20} weight="bold" />
                <span>
                  {accountInfo.balance.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  {accountInfo.currency}
                </span>
              </div>

              {isBelowMin && !isNegative && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Below minimum balance of ${MIN_BALANCE}
                </p>
              )}

              {isNegative && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Balance is negative
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ NEW: Summary Cards (Deposit / Refund) */}
      {
        currentUser?.role?.name !== 'Client' &&
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <SummaryCard
            title="Total Deposits"
            value={totals.totalDeposit}
            currency={displayCurrency}
            icon={<ArrowDownRight size={20} />}
            tone="green"
          />
          <SummaryCard
            title="Total Refunds"
            value={totals.totalRefund}
            currency={displayCurrency}
            icon={<ArrowUpRight size={20} />}
            tone="red"
          />
        </div>
      }

      {/* Top Bar: Search / Filters / Export / Withdraw */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        {/* Search (Admins only) */}
        {currentUser?.role?.name !== "Client" && (
          <div className="relative flex-grow max-w-xs">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
            </div>
            <input
              type="text"
              placeholder="Search transactions..."
              className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 cursor-pointer ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
              onClick={handleSearch}
              aria-label="Submit search"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Date Range */}
        <DateRangePicker
          onRangeChange={handleDateRangeChange}
          value={dateRange}
          className={
            dateRange.start || dateRange.end
              ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
              : ""
          }
        />

        {/* More Filters */}
        <button
          className={`flex items-center ring-yellow-500/50 gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${showMoreFilters
            ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
            : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
            }`}
          onClick={() => setShowMoreFilters(!showMoreFilters)}
        >
          <span className="text-sm">
            {showMoreFilters ? "Hide filters" : "More filters"}
          </span>
          {showMoreFilters ? (
            <X size={16} className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        {/* Reset */}
        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">Reset</span>
        </button>

        {/* Apply Filters */}
        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-sm justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-sm">Apply Filters</span>
          </button>
        )}

        {/* Request Withdrawal (Client with account) */}
        {currentUser?.role?.name === "Client" && hasUserAccount && (
          <>
          </>
        )}
        <button
          onClick={handleRequestWithdrawal}
          className="ml-auto flex items-center gap-2 px-4 h-10 bg-neutral-900 hover:bg-neutral-950 dark:bg-neutral-100 dark:hover:bg-neutral-200 text-white dark:text-black rounded-xl"
        >
          <CurrencyDollar size={16} />
          <span className="text-sm">Request Refund</span>
        </button>
      </div>

      {/* Active Chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            Active filters:
          </span>

          {searchQuery && currentUser?.role?.name !== "Client" && (
            <Chip
              icon={<MagnifyingGlass size={12} />}
              label={searchQuery}
              onClear={() => {
                setSearchQuery("")
                handleSearch()
              }}
            />
          )}

          {(dateRange.start || dateRange.end) && (
            <Chip
              icon={<CalendarBlank size={12} />}
              label={`${dateRange.start ? new Date(dateRange.start).toLocaleDateString() : "Any"} - ${dateRange.end ? new Date(dateRange.end).toLocaleDateString() : "Any"
                }`}
              onClear={() => {
                setDateRange({ start: "", end: "" })
                handleSearch()
              }}
            />
          )}

          {filters.transactionType && (
            <Chip
              label={
                filters.transactionType === "deposit"
                  ? "Deposits"
                  : filters.transactionType === "withdrawal"
                    ? "Refunds"
                    : filters.transactionType
              }
              onClear={() => {
                handleFilterChange("transactionType", "")
                applyFilters()
              }}
            />
          )}

          {filters.status && (
            <Chip
              label={filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
              onClear={() => {
                handleFilterChange("status", "")
                applyFilters()
              }}
            />
          )}

          {filters.paymentMethod && (
            <Chip
              icon={<CreditCard size={12} />}
              label={filters.paymentMethod}
              onClear={() => {
                handleFilterChange("paymentMethod", "")
                applyFilters()
              }}
            />
          )}

          {filters.user && currentUser?.role?.name !== "Client" && (
            <Chip
              icon={<User size={12} />}
              label={
                userOptions.find((u) => u.value === filters.user)?.name || "Selected Client"
              }
              onClear={() => {
                handleFilterChange("user", "")
                applyFilters()
              }}
            />
          )}

          <button
            onClick={resetAndRefresh}
            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* Expanded Filters */}
      {showMoreFilters && (
        <div className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">
              More filters
            </h2>
            <button
              onClick={clearFilters}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
            >
              <X size={14} />
              <span>Clear all</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <SearchableSelect
              options={transactionTypeOptions}
              value={filters.transactionType}
              onChange={(v) => handleFilterChange("transactionType", v)}
              placeholder="Transaction Type"
              icon={<Activity size={16} />}
            />

            <SearchableSelect
              options={statusOptions}
              value={filters.status}
              onChange={(v) => handleFilterChange("status", v)}
              placeholder="Status"
              icon={<Tag size={16} />}
            />

            <SearchableSelect
              options={paymentMethodOptions}
              value={filters.paymentMethod}
              onChange={(v) => handleFilterChange("paymentMethod", v)}
              placeholder="Payment Method"
              icon={<CreditCard size={16} />}
            />

            {currentUser?.role?.name !== "Client" && (
              <SearchableSelect
                options={formattedUserOptions}
                value={filters.user}
                onChange={(v) => handleFilterChange("user", v)}
                placeholder="Client"
                icon={<User size={16} />}
                loading={loadingOptions}
              />
            )}

            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-300"
                onClick={applyFilters}
              >
                <FunnelSimple size={16} />
                <span className="text-sm">Apply Filters</span>
              </button>
              <button
                className="flex items-center justify-center px-4 gap-2 h-9 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700"
                onClick={resetAndRefresh}
              >
                <ArrowClockwise size={16} />
                <span className="text-sm">Reset</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table + Pagination */}
      <div className="border border-neutral-150 dark:border-neutral-900 rounded-lg bg-white dark:bg-neutral-950">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap
              size={30}
              weight="bold"
              className="animate-spin text-neutral-600 dark:text-neutral-400"
            />
          </div>
        ) : transactions.length > 0 ? (
          <>
            {/* Scrollable table */}
            <div className="overflow-x-auto rounded-t-lg">
              <table className="min-w-full divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50/80 backdrop-blur-sm dark:bg-neutral-900/40">
                  <tr>
                    <Th>Date &amp; Time</Th>
                    {currentUser?.role?.name !== "Client" && <Th>Client Reference</Th>}

                    <Th>Type</Th>
                    <Th>Amount</Th>
                    {/* <Th>Currency</Th> */}

                    <Th>Status</Th>
                    {currentUser?.role?.name !== "Client" && <Th>Status Changed By</Th>}
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-neutral-950 divide-y divide-neutral-100 dark:divide-neutral-800 text-start">
                  {transactions.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="hover:bg-neutral-50/80 dark:hover:bg-neutral-900/60 transition-colors"
                    >
                      <Td>
                        <div className="flex flex-col text-sm">
                          <span className="font-[400] text-neutral-800 dark:text-neutral-100">
                            {formatStatusChangeDate(transaction.date)}
                          </span>
                        </div>
                      </Td>
                      {currentUser?.role?.name !== "Client" && (
                        <Td className="max-w-xs truncate">
                          <div className="flex items-center gap-1">
                            <Crown size={14} className="text-amber-500 flex-shrink-0" />
                            <span className="truncate text-sm font-[300]">
                              {transaction.accountName}
                            </span>
                          </div>
                        </Td>
                      )}
                      <Td>
                        <span
                          className={`inline-flex items-center gap-1 text-sm ${transaction.type === "deposit"
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-500"
                            }`}
                        >
                          {transaction.type === "deposit" ? (
                            <>
                              <ArrowDownRight size={16} /> Deposit
                            </>
                          ) : (
                            <>
                              <ArrowUpRight size={16} /> Refund
                            </>
                          )}
                        </span>
                      </Td>

                      <Td
                        className={
                          transaction.type === "deposit"
                            ? "text-green-600 dark:text-green-400 font-[400]"
                            : "text-red-600 dark:text-red-500 font-[400]"
                        }
                      >
                        {transaction.type === "deposit" ? "+" : "-"} $
                        {transaction.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Td>

                      {/* <Td>{transaction.currency}</Td> */}



                      <Td>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-[400] ${getStatusBadgeColor(
                            transaction.status
                          )}`}
                        >
                          <span
                            className={`w-2 h-2 mr-1.5 rounded-full ${getStatusDotClass(
                              transaction.status
                            )}`}
                          ></span>
                          {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                        </span>
                      </Td>

                      {currentUser?.role?.name !== "Client" && (
                        <Td>
                          {transaction.statusChangedBy ? (
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                                <Shield size={12} className="text-purple-600 dark:text-purple-400" />
                              </div>
                              <div>
                                <div className="text-sm text-neutral-800 dark:text-white">
                                  {transaction.statusChangedBy.name}
                                </div>
                                <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
                                  <Clock size={10} />
                                  {formatStatusChangeDate(transaction.statusChangedAt)}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">-</span>
                          )}
                        </Td>
                      )}

                      <Td className="text-sm font-[300] flex gap-2 items-center mt-1.5">
                        <button
                          onClick={() => handleViewTransaction(transaction)}
                          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-600/10 p-1.5 rounded-xl transition-colors"
                        >
                          <Eye size={14} />
                        </button>

                        {currentUser?.role?.name !== "Client" &&
                          transaction.status !== "completed" &&
                          transaction.status !== "rejected" && (
                            <button
                              onClick={() => handleChangeStatus(transaction)}
                              className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 bg-amber-600/10 p-1.5 rounded-xl transition-colors"
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination outside scroll */}
            <div className="px-6 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 bg-neutral-50/60 dark:bg-neutral-900/40 rounded-b-lg">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-[13px] text-neutral-600 dark:text-neutral-400">
                  Showing {transactions.length} of {pagination.totalItems} transactions
                </span>

                <div className="ml-4 flex items-center gap-2">
                  <span className="text-[12px] text-neutral-500 dark:text-neutral-400">
                    Rows
                  </span>
                  <div className="relative">
                    <select
                      value={pagination.pageSize}
                      onChange={handlePageSizeChange}
                      className="h-8 pl-3 pr-8 text-[12px] border border-neutral-200/80 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-neutral-100 shadow-sm focus:outline-none focus:ring-1 focus:ring-neutral-300 dark:focus:ring-neutral-600 appearance-none cursor-pointer"
                    >
                      <option value={5}>5 / page</option>
                      <option value={10}>10 / page</option>
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </select>
                    <CaretDown
                      size={14}
                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(1)}
                  aria="First page"
                >
                  <CaretDoubleLeft size={14} />
                </PageBtn>
                <PageBtn
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  aria="Previous page"
                >
                  <CaretLeft size={14} />
                </PageBtn>

                <div className="flex items-center space-x-1">
                  {getPageNumbers(pagination.currentPage, pagination.totalPages).map((page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 py-1 text-neutral-500 dark:text-neutral-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-7 h-7 flex items-center justify-center rounded-xl text-[13px] ${pagination.currentPage === page
                          ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                          : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                          }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <PageBtn
                  disabled={
                    pagination.currentPage === pagination.totalPages || pagination.totalPages === 0
                  }
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  aria="Next page"
                >
                  <CaretRight size={14} />
                </PageBtn>
                <PageBtn
                  disabled={
                    pagination.currentPage === pagination.totalPages || pagination.totalPages === 0
                  }
                  onClick={() => handlePageChange(pagination.totalPages)}
                  aria="Last page"
                >
                  <CaretDoubleRight size={14} />
                </PageBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>
            <h3 className="text-xl font-[400] text-neutral-900 dark:text-white mb-2">
              No Transactions Found
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {searchQuery ||
                Object.values(filters).some(Boolean) ||
                dateRange.start ||
                dateRange.end
                ? "No transactions match your search criteria. Try adjusting your filters or search term."
                : currentUser?.role?.name === "Client"
                  ? "You haven't made any transactions yet. Start by adding funds to your account."
                  : "There are no transactions in the system yet."}
            </p>
            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <WithdrawalRequestModal
        isOpen={showWithdrawalModal}
        onClose={() => setShowWithdrawalModal(false)}
        accountInfo={accountInfo}
        reset={() => resetAndRefresh()}
      />

      <TransactionDetailModal
        isOpen={showTransactionDetailModal}
        onClose={() => setShowTransactionDetailModal(false)}
        transaction={selectedTransaction}
      />

      <ChangeStatusModal
        isOpen={showChangeStatusModal}
        onClose={() => setShowChangeStatusModal(false)}
        transaction={selectedTransaction}
        reset={() => resetAndRefresh()}
      />
    </div>
  )
}

/* ───────────────────── Small UI helpers ───────────────────── */

function Th({ children }) {
  return (
    <th className="px-6 py-5 text-left text-[12px] font-[400] text-neutral-500 dark:text-neutral-400 uppercase tracking-[0.08em] whitespace-nowrap">
      {children}
    </th>
  )
}

function Td({ children, className = "" }) {
  return (
    <td
      className={`px-6 py-4 whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-200 ${className}`}
    >
      {children}
    </td>
  )
}

function Chip({ label, onClear, icon }) {
  return (
    <div className="flex items-center gap-1 bg-yellow-100 border border-yellow-600/20 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded-full text-xs">
      {icon}
      <span>{label}</span>
      <button onClick={onClear} className="ml-1">
        <X size={12} />
      </button>
    </div>
  )
}

function PageBtn({ disabled, onClick, children, aria }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1 rounded-xl ${disabled
        ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
        : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        }`}
      aria-label={aria}
    >
      {children}
    </button>
  )
}

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
  if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages]
  if (currentPage >= totalPages - 2)
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
}

/* ✅ NEW: Summary Card */
function SummaryCard({ title, value, currency, icon, tone = "neutral" }) {
  const toneMap = {
    green: "bg-white dark:bg-neutral-950 text-green-600 dark:text-green-500 border-neutral-150 dark:border-neutral-900",
    red: "bg-white dark:bg-neutral-950 text-red-600 dark:text-red-500 border-neutral-150 dark:border-neutral-900",
  }

  const pillMap = {
    green: "bg-green-600/10 text-green-700 dark:bg-green-500/10 dark:text-green-600",
    red: "bg-red-600/10 text-red-700 dark:bg-red-500/10 dark:text-red-600",
  }

  return (
    <div className={`border flex items-center justify-between rounded-xl p-5 ${toneMap[tone] || toneMap.neutral}`}>
      <div className="flex   flex-col justify-center items-start">
        <div className="text-xs uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400">
          {title}
        </div>
        <div className="mt-1 text-2xl font-[400]">
          <span>
            {Number(value || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>{" "}
          <span className="text-sm font-[400] ">
            {currency}
          </span>
        </div>

      </div>

      <div className={`p-3 rounded-xl ${pillMap[tone] || pillMap.neutral}`}>
        {icon}
      </div>
    </div>
  )
}

export default BalanceRecords
